import { ErrorSelectors } from '@lukso/lsp-smart-contracts'
import Web3 from 'web3'
import { isAddress, numberToHex } from 'web3-utils'

import LanguageController from '@/background/controllers/LanguageController'
import { hasErrorData } from '@/background/utils/validators'
import { logError } from '@/shared/logger'
import { getFunctionSignature } from '@/shared/getFunctionSignature'
import {
  CustomErrorPartialError,
  DetailedError,
  EmptyRevertPartialError,
  ErrorPartialError,
  ErrorSelector,
  ErrorType,
  ErrorsType,
  FullDetailedError,
  PanicPartialError,
} from '@/shared/models/DetailedError'

/**
 * Cleanup object from undefined values
 */
export function cleanObj(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map((obj: any) => cleanObj(obj))
  }
  if (obj === undefined) {
    return
  }
  if (obj === null) {
    return obj
  }
  if (typeof obj !== 'object') {
    return obj
  }
  return Object.fromEntries(
    Object.entries(obj)
      .map(([k, v]) => [k, cleanObj(v)])
      .filter(([, v]) => v !== undefined)
  )
}

/**
 * toJSON function for Error
 *
 * @param this Error
 * @returns any
 */
function errorToJson(this: FullDetailedError) {
  return cleanObj({
    message: this.message,
    hexSignature: this.hexSignature,
    isEmpty: this.isEmpty,
    data: this.data,
    userdoc: this.userdoc,
    advanced: this.advanced,
    type: this.type,
  })
}

const ERROR = {
  STRING: '0x08c379a0', // keccak256('Error(string)').slice(0, 10)
  PANIC: '0x4e487b71', // keccak256('Panic(uint)').slice(0, 10)
  EMPTY_REVERT: '0xd8b98391', // keccak256('Revert()').slice(0, 10)
}

const CONTRACT_ERROR_THROWN = 'transactions.error.contract_error_thrown'

export const ErrorTypeMap: Record<ErrorType, string> = {
  [ErrorType.INVALID]: 'transaction.error.types.invalid',
  [ErrorType.PANIC]: 'transaction.error.types.panic',
  [ErrorType.ERROR]: 'transaction.error.types.error',
  [ErrorType.CUSTOM_ERROR]: 'transaction.error.types.custom_error',
  [ErrorType.EMPTY_REVERT]: 'transaction.error.types.empty_revert',
}

function populateEmptyRevertInfo(
  error: EmptyRevertPartialError,
  languageController: LanguageController,
  hexSignature: string
) {
  error.type = ErrorType.EMPTY_REVERT
  error.message = languageController.t('transactions.error.reverted')
  error.hexSignature = hexSignature
  error.isEmpty = true
  error.advanced = {
    sig: 'Revert()',
    name: 'Revert',
    inputs: [],
    devdoc: languageController.t('transactions.error.empty_revert.devdoc'),
  }
}

function populateErrorInfo(
  error: ErrorPartialError,
  languageController: LanguageController,
  web3: Web3,
  hexSignature: string,
  message: string
) {
  const value = web3.eth.abi.decodeParameter(
    'string',
    '0x' + message
  ) as unknown as string
  error.message = languageController.t(CONTRACT_ERROR_THROWN)
  error.advanced = {
    sig: 'Error(string)',
    name: 'Error',
    inputs: [{ type: 'string', value: value, doc: 'Error message string' }],
    devdoc: value,
  }
  error.hexSignature = hexSignature
  error.toJSON = errorToJson.bind(error)
  error.type = ErrorType.ERROR
}

function populatePanicInfo(
  error: PanicPartialError,
  languageController: LanguageController,
  web3: Web3,
  hexSignature: string,
  message: string
) {
  error.type = ErrorType.PANIC
  const value = web3.eth.abi.decodeParameter('uint', '0x' + message)
  const data = numberToHex(value as unknown as string)
  error.message = languageController.t(CONTRACT_ERROR_THROWN)
  error.advanced = {
    sig: 'Panic(uint)',
    name: 'Panic',
    inputs: [{ type: 'uint', value: data, doc: 'Panic code value' }],
    devdoc: languageController.t(`transactions.error.panic-${data}`),
  }
  error.hexSignature = hexSignature
  error.toJSON = errorToJson.bind(error)
}

// eslint-disable-next-line sonarjs/cognitive-complexity
function attemptToPopulateCustomErrorInfo(
  error: CustomErrorPartialError,
  web3: Web3,
  message: string,
  errorSelector: ErrorSelector
): boolean {
  // Found the error signature in LSP
  try {
    const { sig, name, inputs } = errorSelector

    const params: string[] = inputs.map(({ type }) => type)

    // Decode arguments according to function spec
    const values = web3.eth.abi.decodeParameters(params, message)

    // Build arg list (if there are bool values which are no 0 or 1 we can assume it's invalid)
    // Other values are validated in encodeParameters.
    const encodeArgs = Array.from({ length: params.length }).map(
      (_val, index) => {
        if (params[index] === 'bool') {
          const val = values[`${index}`]
          if (val != 1 && val != 0) {
            // Due to javascript truthyness any non-empty or non-zero value is encoded as true without a problem.
            // The output packet won't match but there is really no reason to even try.
            throw new Error('Invalid boolean value')
          }
        }
        if (params[index] === 'address') {
          const val = values[`${index}`]
          if (!isAddress(val)) {
            throw new Error('Invalid address value' + val)
          }
        }
        return values[`${index}`] ?? '0x'
      }
    )

    // Re-encode according to function spec.
    const newData = web3.eth.abi.encodeParameters(params, encodeArgs).slice(2)

    if (message === newData) {
      error.type = ErrorType.CUSTOM_ERROR
      // Decoding and re-encoding worked, we can assume this is the correct error.
      // Fill in the abi with it's values.
      error.advanced = {
        sig,
        name,
        inputs: inputs.map((item, index) => {
          const { name } = item
          const value = values[name] || values[`${index}`]
          return { ...item, value }
        }),
      }

      // NOTE: These functions rely on error.advanced so it needs to be filled in first

      // NOTE: There were older versions of LSP contracts compiled where the userdoc array contained details instead of notice
      const {
        userdoc: [
          {
            details: _legacyUserDetails = undefined,
            notice: userNotice = undefined,
          } = {},
        ] = [],
        devdoc: [{ details: developmentDetails = undefined } = {}] = [],
      } = errorSelector as any

      if (developmentDetails) {
        // Fill in dynamic expressions inside of devdoc if either exist.
        // error.advanced is set for sure from above.
        error.advanced.devdoc = fillTransactionErrorDocs(
          error as DetailedError,
          developmentDetails
        )
      }
      const userDocSource = userNotice || _legacyUserDetails
      if (userDocSource) {
        // Fill in dynamic expressions inside of userdoc if either exist.
        error.userdoc = fillTransactionErrorDocs(
          error as DetailedError,
          userDocSource
        )
      }

      // Set the error type to custom error
      error.toJSON = errorToJson.bind(error as FullDetailedError)
      return true
    } else {
      // This is not the correct errorSelector
      return false
    }
  } catch (err) {
    logError(`Error while attempting to populate custom error infor:`, err)
    // This excepted so for sure not the correct errorSelector
    return false
  }
}

/**
 * Decode network error if there is one and amend it with information
 * about the error to be passed on to the rest of the code.
 *
 * @param languageController Langague controller to access yata
 * @param web3 Web3 to be able to access abi decoder/encoder
 * @param error Original error
 * @returns Promise<void>
 */
export const decodeError = async (
  languageController: LanguageController,
  web3: Web3,
  error?: DetailedError
  // eslint-disable-next-line sonarjs/cognitive-complexity
): Promise<void> => {
  if (!error) {
    return
  }
  if (!hasErrorData(error)) {
    // This is not a network error, return
    return
  }
  if (error.toJSON) {
    // Already decoded, return
    return
  }

  // Extract the error data
  const hexSignature = error.data.slice(0, 10)
  const message = error.data.slice(10)

  switch (hexSignature) {
    case ERROR.EMPTY_REVERT: {
      populateEmptyRevertInfo(
        error as unknown as EmptyRevertPartialError,
        languageController,
        hexSignature
      )
      return
    }
    case ERROR.STRING: {
      populateErrorInfo(
        error as ErrorPartialError,
        languageController,
        web3,
        hexSignature,
        message
      )
      return
    }
    case ERROR.PANIC: {
      populatePanicInfo(
        error as PanicPartialError,
        languageController,
        web3,
        hexSignature,
        message
      )
      return
    }

    default: {
      // Lookup error inside of LSP
      const lspErrors = Object.values(
        ErrorSelectors as unknown as ErrorsType
      ).find(v => {
        return v[hexSignature]
      })
      const errorSelector = lspErrors ? lspErrors[hexSignature] : undefined
      error.message = languageController.t(CONTRACT_ERROR_THROWN)

      error.hexSignature = hexSignature
      if (
        errorSelector &&
        attemptToPopulateCustomErrorInfo(
          error as CustomErrorPartialError,
          web3,
          message,
          errorSelector
        )
      ) {
        return
      }
      // Try to find the error inside of 4byte
      const signature = await getFunctionSignature(
        web3.eth,
        error.data,
        true
      ).catch(() => {})

      if (signature) {
        attemptToPopulateCustomErrorInfo(
          error as CustomErrorPartialError,
          web3,
          message,
          {
            sig: signature,
            name: signature.replace(/\(.*\)/, ''),
            type: 'error',
            inputs: signature
              .replace(/.*\((.*)\).*/, '$1')
              .split(',')
              .map(v => {
                const [type, name] = v.split(' ')
                if (name) {
                  return { type, name }
                }
                return { type }
              }),
          }
        )
      }
    }
  }
}

/**
 * Handles an error after failing the keyManager's estimateGas method.
 *
 * @param Function callExecute - function to call
 * @returns string - error message
 */
export const wrapNetworkCallWithErrorDecoder = async (
  languageController: LanguageController,
  web3: Web3,
  callExecute: () => Promise<any>
): Promise<any> => {
  try {
    return await callExecute()
  } catch (error) {
    await decodeError(languageController, web3, error as DetailedError)
    throw error
  }
}

/**
 * Can fill in `from`, `to`, and `value` in a transaction object's userdoc and devdoc.
 *
 * @param transaction
 * @param value
 * @returns
 */
function fillTransactionErrorDocs(error: DetailedError, value?: string) {
  if (!value) {
    return // If the docs are empty or undefined, return undefined.
  }
  const { advanced: { inputs } = {} } = error as FullDetailedError
  return value.replaceAll(/`([_a-z]*)`/gi, (_, key: string) => {
    const value = inputs?.find(({ name }) => name === key)?.value
    if (value === undefined) {
      return `\`${key}\``
    }
    return `\`${value}\``
  })
}
